import { useCallback, useEffect } from 'react'

import {
  useAuth as useClerkAuth,
  useOrganization,
  useOrganizationList,
  useSession,
} from '@clerk/clerk-react'
import Intercom from '@intercom/messenger-js-sdk'
import OneSignal from 'react-onesignal'

import { Redirect, routes } from '@cedarjs/router'

import { useAuth } from 'src/auth'
import { useDispatch } from 'src/hooks/useDispatch'
import { useOrgName } from 'src/hooks/useOrgName'
import useUserId from 'src/hooks/useUserId'
import datadogRum from 'src/lib/datadogRum'
import Sentry from 'src/lib/sentry'
import { reset } from 'src/sharedActions'
import { clearClerkData, setClerkData } from 'src/slices/clerk'
import { AbilityLoader } from 'src/utils/AbilityLoader'

type AuthenticatorLayoutProps = {
  children?: React.ReactNode
}

const AuthenticatorLayout = ({ children }: AuthenticatorLayoutProps) => {
  const dispatch = useDispatch()
  const { loading: isAuthLoading, isAuthenticated, currentUser } = useAuth()
  const { session } = useSession()
  const [switchOrgInProgress, setSwitchOrgInProgress] =
    React.useState<boolean>(false)
  const {
    isLoaded: isOrgLoaded,
    organizationList,
    setActive,
  } = useOrganizationList()
  const { orgSlug: activeOrgSlug, isLoaded: isClerkAuthLoaded } = useClerkAuth()
  const urlOrgSlug = useOrgName()
  const [oneSignalInitialized, setOneSignalInitialized] = React.useState(false)
  const { id: userId, hasLoaded: userIdHasLoaded } = useUserId()

  // Initialize Sentry and Datadog, and Intercom
  useEffect(() => {
    if (!isAuthenticated || !currentUser || !userIdHasLoaded) {
      return
    }

    Sentry.setUser(currentUser)
    datadogRum.setUser(currentUser)

    if (process.env.INTERCOM_APP_ID) {
      Intercom({
        app_id: process.env.INTERCOM_APP_ID,
        user_id: currentUser.id as string,
        name: currentUser.username as string,
        email: currentUser.email as string,
        created_at: currentUser.createdAt as number,
      })
    }
  }, [currentUser, isAuthenticated, userIdHasLoaded, userId])

  // Initialize OneSignal
  useEffect(() => {
    const initializeOneSignal = async () => {
      await OneSignal.init({
        appId: process.env.ONESIGNAL_APP_ID,
        allowLocalhostAsSecureOrigin: true,
        webhooks: {
          cors: true, // Defaults to false if omitted
        },
      })
      await OneSignal.login(userId)
      await OneSignal.User.addEmail(currentUser.email as string)
      if (!OneSignal.Notifications.permission) {
        await OneSignal.Notifications.requestPermission()
      }
    }
    if (
      currentUser &&
      !oneSignalInitialized &&
      isAuthenticated &&
      userId &&
      userIdHasLoaded
    ) {
      setOneSignalInitialized(true)
      initializeOneSignal()
    }
  }, [
    currentUser,
    oneSignalInitialized,
    isAuthenticated,
    userId,
    userIdHasLoaded,
  ])

  const org = useOrganization()

  // Touch the session to keep it alive
  useEffect(() => {
    if (session) {
      session.touch()
    }
  }, [activeOrgSlug])

  useEffect(() => {
    if (org.isLoaded) {
      dispatch(
        setClerkData({
          permissions: org?.membership?.permissions ?? [],
          isLoaded: org.isLoaded,
        })
      )
    }

    return () => {
      dispatch(clearClerkData())
    }
  }, [org, dispatch])

  const unauthorized = useCallback(
    () =>
      !(
        isAuthenticated &&
        organizationList.some((org) => org.organization.slug === urlOrgSlug)
      ),
    [isAuthenticated, organizationList, urlOrgSlug]
  )
  const setActiveOrg = useCallback(async () => {
    if (activeOrgSlug !== urlOrgSlug) {
      setSwitchOrgInProgress(true)
      const org = organizationList.find(
        (org) => org.organization.slug === urlOrgSlug
      )
      if (org) {
        setActive({ organization: org.organization.id }).finally(() => {
          setSwitchOrgInProgress(false)
        })
      } else {
        setSwitchOrgInProgress(false)
      }
    }
    return true
  }, [activeOrgSlug, urlOrgSlug, organizationList, setActive])

  if (
    isAuthLoading ||
    !isOrgLoaded ||
    !isClerkAuthLoaded ||
    !org.isLoaded ||
    switchOrgInProgress
  ) {
    return null
  }

  if (unauthorized()) {
    return <Redirect to={routes.forbidden()} />
  }

  // When the User is authorized make sure the active org is set
  setActiveOrg()
  return (
    <AbilityLoader
      orgSlug={activeOrgSlug}
      userId={currentUser?.providerId as string}
    >
      {children}
    </AbilityLoader>
  )
}

export default AuthenticatorLayout
